import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Paperclip, X, PaperPlaneTilt, CheckCircle, Images, ClipboardText } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { engApi } from './engApi';
import { CameraCaptureDialog } from './CameraCaptureDialog';

const isAnswered = (v) => !(v == null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0));

function FileAnswer({ field, value = [], onChange, formId }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const isPhoto = field.type === 'photo';

  const upload = async (file) => {
    setUploading(true);
    try {
      const res = await engApi.uploadFormFile(formId, file);
      onChange([...value, { file_id: res.file_id, filename: res.filename }]);
      toast.success(isPhoto ? '写真を追加しました' : 'ファイルを添付しました');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'アップロードに失敗しました');
    } finally { setUploading(false); }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) upload(file);
  };

  return (
    <div className="space-y-3 mt-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((v, i) => (
            <div key={v.file_id} className="relative" data-testid={`eng-fill-file-chip-${field.id}-${i}`}>
              {isPhoto ? (
                <img src={engApi.formFileUrl(v.file_id)} alt={v.filename}
                  className="h-24 w-24 object-cover rounded-xl border border-zinc-200 shadow-sm" />
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-100 text-xs text-zinc-700 max-w-[200px] truncate">
                  <Paperclip size={13} /> {v.filename}
                </span>
              )}
              <button onClick={() => onChange(value.filter((x) => x.file_id !== v.file_id))}
                className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-1 shadow-md active:scale-90 transition-transform"
                data-testid={`eng-fill-file-remove-${field.id}-${i}`} aria-label="削除">
                <X size={11} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
      {isPhoto ? (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" disabled={uploading} onClick={() => setCameraOpen(true)}
            className="flex flex-col items-center justify-center gap-1.5 h-20 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/50 text-teal-700 hover:bg-teal-50 active:scale-[0.98] transition-all disabled:opacity-50"
            data-testid={`eng-fill-camera-${field.id}`}>
            <Camera size={24} weight="duotone" />
            <span className="text-xs font-semibold">{uploading ? '送信中…' : 'カメラで撮影'}</span>
          </button>
          <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()}
            className="flex flex-col items-center justify-center gap-1.5 h-20 rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50/50 text-zinc-600 hover:bg-zinc-50 active:scale-[0.98] transition-all disabled:opacity-50"
            data-testid={`eng-fill-upload-${field.id}`}>
            <Images size={24} weight="duotone" />
            <span className="text-xs font-semibold">ファイルから選択</span>
          </button>
        </div>
      ) : (
        <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()}
          className="flex items-center justify-center gap-2 w-full h-14 rounded-xl border-2 border-dashed border-teal-300 bg-teal-50/50 text-teal-700 hover:bg-teal-50 active:scale-[0.99] transition-all disabled:opacity-50 text-sm font-semibold"
          data-testid={`eng-fill-upload-${field.id}`}>
          <Paperclip size={18} /> {uploading ? '送信中…' : 'ファイルを添付'}
        </button>
      )}
      <input ref={inputRef} type="file" className="hidden" onChange={onPick}
        accept={isPhoto ? 'image/*' : undefined} data-testid={`eng-fill-file-input-${field.id}`} />
      <CameraCaptureDialog open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={upload} />
    </div>
  );
}

function FieldInput({ field, value, onChange, formId }) {
  const big = 'h-12 mt-2 text-base';
  switch (field.type) {
    case 'textarea':
      return <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={field.placeholder || ''} className="mt-2 text-base" data-testid={`eng-fill-input-${field.id}`} />;
    case 'number':
      return <Input type="number" inputMode="decimal" value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ''} className={`${big} tabular-nums`} data-testid={`eng-fill-input-${field.id}`} />;
    case 'date':
      return <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className={big} data-testid={`eng-fill-input-${field.id}`} />;
    case 'time':
      return <Input type="time" value={value || ''} onChange={(e) => onChange(e.target.value)} className={big} data-testid={`eng-fill-input-${field.id}`} />;
    case 'select':
      return (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)}
          className="w-full h-12 mt-2 rounded-lg border border-zinc-200 bg-white px-3 text-base outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
          data-testid={`eng-fill-input-${field.id}`}>
          <option value="">選択してください…</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case 'radio':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid={`eng-fill-input-${field.id}`}>
          {(field.options || []).map((o) => (
            <label key={o}
              className={`flex items-center gap-2.5 px-4 py-3.5 rounded-xl border-2 cursor-pointer transition-all active:scale-[0.99] ${
                value === o ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}>
              <input type="radio" name={field.id} checked={value === o} onChange={() => onChange(o)} className="accent-teal-600 w-4 h-4" />
              <span className={`text-sm font-medium ${value === o ? 'text-teal-800' : 'text-zinc-700'}`}>{o}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox':
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2" data-testid={`eng-fill-input-${field.id}`}>
          {(field.options || []).map((o) => {
            const arr = Array.isArray(value) ? value : [];
            const on = arr.includes(o);
            return (
              <label key={o}
                className={`flex items-center gap-2.5 px-4 py-3.5 rounded-xl border-2 cursor-pointer transition-all active:scale-[0.99] ${
                  on ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-zinc-200 bg-white hover:border-zinc-300'}`}>
                <Checkbox checked={on} onCheckedChange={(c) => onChange(c ? [...arr, o] : arr.filter((x) => x !== o))} />
                <span className={`text-sm font-medium ${on ? 'text-teal-800' : 'text-zinc-700'}`}>{o}</span>
              </label>
            );
          })}
        </div>
      );
    case 'photo':
    case 'file':
      return <FileAnswer field={field} value={Array.isArray(value) ? value : []} onChange={onChange} formId={formId} />;
    default:
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder || ''} className={big} data-testid={`eng-fill-input-${field.id}`} />;
  }
}

export default function EngFormFill({ standalone = false }) {
  const navigate = useNavigate();
  const { fid } = useParams();
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    engApi.form(fid).then(setForm).catch((e) => setErr(e?.response?.data?.detail || 'フォームの取得に失敗しました'));
  }, [fid]);

  const fields = useMemo(() => form?.fields || [], [form]);
  const answeredCount = useMemo(() => fields.filter((f) => isAnswered(answers[f.id])).length, [fields, answers]);
  const progress = fields.length ? Math.round((answeredCount / fields.length) * 100) : 0;

  const submit = async () => {
    for (const f of fields) {
      if (f.required && !isAnswered(answers[f.id])) {
        toast.error(`「${f.label}」は必須です`);
        document.getElementById(`fill-field-${f.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    setSubmitting(true);
    try {
      await engApi.submitFormResponse(fid, answers);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '送信に失敗しました');
    } finally { setSubmitting(false); }
  };

  const inner = err ? (
    <div className="py-20 text-center" data-testid="eng-fill-error">
      <ClipboardText size={44} weight="duotone" className="text-zinc-300 mx-auto mb-3" />
      <p className="text-sm text-rose-600">{err}</p>
    </div>
  ) : !form ? (
    <div className="py-20 text-center text-sm text-zinc-400">読込中…</div>
  ) : done ? (
    <div className="py-16 text-center" data-testid="eng-fill-done">
      <CheckCircle size={64} weight="duotone" className="text-teal-500 mx-auto mb-4" />
      <h2 className="text-xl font-bold text-zinc-900">回答を送信しました</h2>
      <p className="text-sm text-zinc-500 mt-1.5">ご協力ありがとうございます。</p>
      <div className="mt-6 flex justify-center gap-3">
        <Button variant="outline" onClick={() => { setAnswers({}); setDone(false); window.scrollTo(0, 0); }}
          className="h-12 px-6" data-testid="eng-fill-again">
          もう一度回答する
        </Button>
        {!standalone && (
          <Button onClick={() => navigate('/engineering/forms')} className="h-12 px-6 bg-teal-700 hover:bg-teal-800" data-testid="eng-fill-back-to-list">
            一覧へ戻る
          </Button>
        )}
      </div>
    </div>
  ) : (
    <>
      {/* Header card */}
      <div className="rounded-lg text-white p-5 shadow-md mb-5" style={{ backgroundImage: 'linear-gradient(135deg, #0D9488, #0E7490)' }}>
        <div className="flex items-start gap-3">
          {!standalone && (
            <button onClick={() => navigate('/engineering/forms')} className="p-1.5 -ml-1 rounded-lg hover:bg-white/15 transition-colors shrink-0" data-testid="eng-fill-back">
              <ArrowLeft size={20} />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold leading-snug">{form.title}</h1>
            {form.description && <p className="text-[13px] text-white/85 mt-1 whitespace-pre-wrap">{form.description}</p>}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-white/80 mb-1">
            <span>回答状況</span>
            <span className="tabular-nums" data-testid="eng-fill-progress-text">{answeredCount} / {fields.length}</span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-4 pb-28">
        {fields.map((f, i) => {
          const ok = isAnswered(answers[f.id]);
          return (
            <div key={f.id} id={`fill-field-${f.id}`}
              className={`bg-white rounded-lg border-2 p-4 sm:p-5 transition-colors ${ok ? 'border-teal-200' : 'border-zinc-100'}`}
              data-testid={`eng-fill-field-${i}`}>
              <div className="flex items-start gap-2.5">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                  ok ? 'bg-teal-600 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                  {ok ? <CheckCircle size={16} weight="bold" /> : i + 1}
                </span>
                <p className="text-[15px] font-semibold text-zinc-800 leading-snug pt-0.5">
                  {f.label}
                  {f.required && <span className="ml-1.5 align-middle text-[10px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">必須</span>}
                </p>
              </div>
              <div className="sm:pl-9">
                <FieldInput field={f} value={answers[f.id]} formId={fid}
                  onChange={(v) => setAnswers((a) => ({ ...a, [f.id]: v }))} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Sticky submit bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/90 backdrop-blur border-t border-zinc-200 p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
        <div className="max-w-xl mx-auto">
          <Button onClick={submit} disabled={submitting}
            className="w-full h-13 min-h-[52px] bg-teal-700 hover:bg-teal-800 text-base font-bold rounded-xl shadow-lg shadow-teal-600/20"
            data-testid="eng-fill-submit">
            <PaperPlaneTilt size={19} className="mr-2" /> {submitting ? '送信中…' : `回答を送信（${answeredCount}/${fields.length}）`}
          </Button>
        </div>
      </div>
    </>
  );

  if (standalone) {
    return (
      <div className="min-h-screen bg-zinc-50" data-testid="eng-form-fill-standalone">
        <div className="max-w-xl mx-auto px-4 py-5">{inner}</div>
      </div>
    );
  }
  return <div className="max-w-xl" data-testid="eng-form-fill">{inner}</div>;
}
